// Универсальные обёртки для использования блочной пагинации и «спекулятивных страниц»
// совместно с любым RTK Query createApi эндпоинтом.
// Цель: минимум кода в хук/странице, возможность маппить любые фильтры в аргументы запроса.

import { computeBlockParams, slicePageFromBlock } from './block-request';
import { calcSpeculativePages, SpeculativeResult } from './speculative-pages';
import { makeScopeKey } from './paged-list-cache';

export type BlockPagerBuildArgsInput<TArgs, TFilters> = {
  page: number;
  perPage: number;
  rawPrefetchPages?: number;
  maxBackendLimit?: number;
  filters?: TFilters;
  // Преобразовать произвольные фильтры в аргументы эндпоинта (без limit/offset)
  mapFiltersToArgs: (filters: TFilters | undefined) => Partial<TArgs>;
  // Базовые аргументы, которые всегда добавляем (опционально)
  baseArgs?: Partial<TArgs>;
};

export type BlockPagerBuildArgsResult<TArgs> = {
  queryArgs: TArgs & { limit: number; offset: number };
  pageIndexInsideBlock: number;
  blockOffset: number;
  blockLimit: number;
};

export function buildBlockQueryArgs<TArgs extends Record<string, any>, TFilters = unknown>(
  input: BlockPagerBuildArgsInput<TArgs, TFilters>,
): BlockPagerBuildArgsResult<TArgs> {
  const { page, perPage, rawPrefetchPages, maxBackendLimit, filters, mapFiltersToArgs, baseArgs } = input;

  const { blockLimit, blockOffset, pageIndexInsideBlock } = computeBlockParams({
    page,
    perPage,
    rawPrefetchPages,
    maxBackendLimit,
  });

  const argsFromFilters = mapFiltersToArgs(filters);
  const queryArgs = {
    ...(baseArgs as any),
    ...(argsFromFilters as any),
    limit: blockLimit,
    offset: blockOffset,
  } as TArgs & { limit: number; offset: number };

  return { queryArgs, pageIndexInsideBlock, blockOffset, blockLimit };
}

export function derivePageFromBlock<T>(block: T[] | undefined, pageIndexInsideBlock: number, perPage: number): T[] {
  return slicePageFromBlock(block, pageIndexInsideBlock, perPage);
}

export type CalcSpecPagesGuardedInput = {
  scopeKey: string;
  page: number;
  perPage: number;
  blockOffset: number;
  blockLength: number;
  requestedLimit?: number;
  isLoading?: boolean;
};

export function calcSpecPagesGuarded(input: CalcSpecPagesGuardedInput): SpeculativeResult {
  const spec = calcSpeculativePages({
    scopeKey: input.scopeKey,
    page: input.page,
    perPage: input.perPage,
    blockOffset: input.blockOffset,
    blockLength: input.blockLength,
    requestedLimit: input.requestedLimit,
  });

  // Пока идёт загрузка — не подтверждаем конец данных (исключаем ложный кламп)
  if (input.isLoading) {
    return { ...spec, provenNoMore: false };
  }

  // Если конец данных подтверждён для текущего ответа — сразу скрываем гипотетическую страницу,
  // не дожидаясь commitSpeculativeObservation (это важно для "далёких" страниц типа page=49)
  if (spec.provenNoMore) {
    return { ...spec, pages: spec.maxKnownPages, hasMore: false };
  }

  return spec;
}

export function makeScopeKeyForFilters(entity: string, filters: Record<string, any>): string {
  return makeScopeKey({ entity, ...filters });
}
